// src/components/NextVisitContext.tsx
import React, { createContext, useState, useContext, useEffect, useCallback, ReactNode } from 'react';
import { message } from 'antd';
import { NextVisit, Patient } from './type';
import { auth } from '../firebaseConfig';
import { cacheOwner } from '../offline/profileCache';
import { queuePatientReminder, pendingWaiting } from '../offline/waitingQueue';
import { 
  getFirestore, 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  deleteDoc, 
  query,
  orderBy,
  where
} from 'firebase/firestore';

interface NextVisitContextType {
  nextVisits: NextVisit[];
  loading: boolean;
  addNextVisit: (patient: Patient, visitDate: string, notes?: string, reminderDurationDays?: number) => Promise<boolean>;
  deleteNextVisit: (visitId: string) => Promise<boolean>;
  fetchNextVisits: () => Promise<NextVisit[]>;
}

const NextVisitContext = createContext<NextVisitContextType | undefined>(undefined);

export const NextVisitProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [nextVisits, setNextVisits] = useState<NextVisit[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // Fetch next visits from Firestore for the current session doctor only
  const fetchNextVisits = useCallback(async (): Promise<NextVisit[]> => {
    try {
      setLoading(true);
      // localStorage is restored synchronously, while Firebase Auth restores its
      // persisted user asynchronously. Reading Firestore in between makes a
      // valid dashboard session look unauthenticated on a cold page load.
      await auth.authStateReady();
      const doctorId = localStorage.getItem('doctorId');
      if (!doctorId || !auth.currentUser) {
        setNextVisits([]);
        return [];
      }

      const db = getFirestore();
      const visitsCollectionRef = collection(db, 'nextVisits', doctorId, 'appointments');
      const visitsQuery = query(visitsCollectionRef, orderBy('visitDate', 'asc'));

      const querySnapshot = await getDocs(visitsQuery);
      const visits: NextVisit[] = [];

      querySnapshot.forEach((docSnap) => {
        visits.push({
          _id: docSnap.id,
          ...docSnap.data()
        } as NextVisit);
      });

      // Keep only rows that belong to this doctor (path should already scope; filter fixes bad/migrated data)
      const scoped = visits.filter(
        (v) => v.doctorId == null || String(v.doctorId) === String(doctorId)
      );
      if (cacheOwner()) {
        try {
          const pending = await pendingWaiting();
          for (const action of pending.filter((item: any) => item.kind === 'reminder')) {
            if (!scoped.some(item => item._id === action.operationId)) scoped.push({ _id: action.operationId, ...action.value, notes: `${action.value.notes || ''} [Pending sync]` });
          }
        } catch (storageError) {
          // The cloud list is already valid. A blocked/private IndexedDB must
          // not turn a local pending-reminder problem into a cloud load error.
          console.warn('Offline reminder storage is unavailable:', storageError);
        }
      }

      console.log('Fetched visits from Firestore:', scoped);
      setNextVisits(scoped);
      return scoped;
    } catch (error) {
      console.error('Error fetching next visits:', error);
      const code = typeof error === 'object' && error !== null && 'code' in error
        ? String((error as { code?: unknown }).code || '')
        : '';
      // Losing connectivity is not a failed appointment load. Keep the last
      // rendered/cache-backed list and retry when the browser comes online.
      if (navigator.onLine && code !== 'unavailable') {
        message.error('Failed to load upcoming appointments');
      }
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Add a new next visit to Firestore
  const addNextVisit = async (
    patient: Patient, 
    visitDate: string, 
    notes?: string, 
    reminderDurationDays?: number
  ): Promise<boolean> => {
    try {
      setLoading(true);
      const doctorId = localStorage.getItem('doctorId');
      const doctorName = localStorage.getItem('doctorName') || localStorage.getItem('username') || '';
      
      if (!doctorId) {
        message.error('Doctor ID not found. Please log in again.');
        return false;
      }

      if (!patient || !patient._id) {
        message.error('Patient information is incomplete');
        return false;
      }
      
      console.log('Adding next visit with:', { patient, visitDate, notes, reminderDurationDays });
      
      const db = getFirestore();
      const nextVisitsCollection = collection(db, 'nextVisits', doctorId, 'appointments');
      
      // Get patient's FCM token from users collection if not in patient object
      let fcmToken = patient.fcmToken;
      if (!fcmToken && (navigator.onLine || !cacheOwner())) {
        try {
          const usersRef = collection(db, 'users');
          const userQuery = await getDocs(
            query(usersRef, where('phone', '==', patient.patient_phone))
          );
          if (!userQuery.empty) {
            fcmToken = userQuery.docs[0].data().fcmToken;
          }
        } catch (error) {
          console.warn('Could not fetch FCM token from users collection:', error);
        }
      }
      
      const nextVisit = {
        patientId: patient._id,
        patientName: patient.patient_name,
        patientPhone: patient.patient_phone,
        doctorId,
        doctorName,
        visitDate,
        reminderDurationDays: reminderDurationDays || 7, // Default to 7 days
        notes: notes || '',
        notificationSent3Days: false, // Track 3-day reminder
        notificationSent1Day: false, // Track 1-day reminder
        notificationDelivered3Days: false, // Track delivery status for 3-day reminder
        notificationDelivered1Day: false, // Track delivery status for 1-day reminder
        createdAt: new Date().toISOString(),
        fcmToken: fcmToken || ''
      };
      
      console.log('Next visit object created:', nextVisit);

      if (!navigator.onLine && cacheOwner()) {
        await queuePatientReminder(nextVisit);
        message.warning('Reminder saved on this device — scheduling pending synchronization.');
        void fetchNextVisits();
        return true;
      }
      
      const docRef = await addDoc(nextVisitsCollection, nextVisit);
      console.log('Document written to Firestore with ID:', docRef.id);
      
      message.success('Visit reminder scheduled successfully');
      await fetchNextVisits(); // Refresh the visits list
      return true;
    } catch (error) {
      console.error('Error adding next visit:', error);
      message.error('Failed to schedule next visit');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Delete a next visit from Firestore
  const deleteNextVisit = async (visitId: string): Promise<boolean> => {
    try {
      setLoading(true);
      const doctorId = localStorage.getItem('doctorId');
      if (!doctorId) return false;
      
      const db = getFirestore();
      const visitDocRef = doc(db, 'nextVisits', doctorId, 'appointments', visitId);
      
      await deleteDoc(visitDocRef);
      console.log('Visit document deleted with ID:', visitId);
      
      message.success('Visit removed from schedule');
      await fetchNextVisits(); // Refresh the visits list
      return true;
    } catch (error) {
      console.error('Error deleting next visit:', error);
      message.error('Failed to remove scheduled visit');
      return false;
    } finally {
      setLoading(false);
    }
  };
  
  // Load visits on mount and whenever the logged-in doctor changes (login, clinic switch, logout)
  // Only doctorIdChanged is needed — Login now dispatches a single event, and
  // assistantIdChanged was causing a duplicate Firestore read on every login.
  useEffect(() => {
    void fetchNextVisits();
    const reload = () => {
      void fetchNextVisits();
    };
    window.addEventListener('doctorIdChanged', reload);
    window.addEventListener('storage', reload);
    window.addEventListener('online', reload);
    return () => {
      window.removeEventListener('doctorIdChanged', reload);
      window.removeEventListener('storage', reload);
      window.removeEventListener('online', reload);
    };
  }, [fetchNextVisits]);
  
  const value = {
    nextVisits,
    loading,
    fetchNextVisits,
    addNextVisit,
    deleteNextVisit
  };

  return (
    <NextVisitContext.Provider value={value}>
      {children}
    </NextVisitContext.Provider>
  );
};

// Custom hook for using the NextVisit context
export const useNextVisits = () => {
  const context = useContext(NextVisitContext);
  if (context === undefined) {
    throw new Error('useNextVisits must be used within a NextVisitProvider');
  }
  return context;
};
